import { Injectable } from '@angular/core';
import {
  InAppBrowser,
  InAppBrowserObject
} from '@ionic-native/in-app-browser/ngx';
import { Platform } from '@ionic/angular';
import { stopVideos } from '@utils/utils';
import { getQueryParamsObject } from '../../utils/utils';
import { LogService } from './log.service';

interface Handler {
  url: string;
  handlerFunction: (resultDetails: any, payloadData?: any) => void;
}
@Injectable({
  providedIn: 'root'
})
export class ExternalUrlOpenerHelperService {
  private defaultIosBrowserOptions =
    'hidenavigationbuttons=yes,toolbar=no,footer=no,location=no,useWideViewPort=no,clearcache=yes';
  private defaultAndroidBrowserOptions =
    'hidenavigationbuttons=yes,hideurlbar=yes,toolbar=no,footer=no,location=no,useWideViewPort=no,clearcache=yes';

  private iabObject: InAppBrowserObject;
  private resultDetails: any;

  private handlers: Handler[] = [];
  private resultHandler: Handler;
  private onViewClosedHandler: (resultDetails: null, payloadData?: any) => void;
  private payloadData: any;

  constructor(
    private platform: Platform,
    private iab: InAppBrowser,
    private log: LogService
  ) {}

  openExternalUrl(
    url: string | URL,
    handlers?: Handler[],
    onViewClosed?: (resultDetails: null, payloadData?: any) => void,
    browserOptions?: {
      ios?: string;
      android?: string;
      web?: {
        sameTab: boolean;
      };
    },
    payloadData?: any
  ) {
    stopVideos();
    const normalizedUrl = typeof url === 'string' ? url : url.href;
    this.log.d(this, 'Url opening has been requested:', normalizedUrl);
    if (!this.platform.is('cordova')) {
      const { web } = browserOptions || ({} as any);
      const { sameTab } = web || ({} as any);
      return window.open(normalizedUrl, sameTab ? '_self' : '_blank');
    }

    this.handlers = handlers && handlers.length ? handlers : [];
    this.onViewClosedHandler = onViewClosed ? onViewClosed : null;
    this.payloadData = payloadData;

    this.platform.ready().then(() => {
      let iabOptions = '';
      if (this.platform.is('ios')) {
        iabOptions =
          browserOptions &&
          (browserOptions.ios !== null || browserOptions.ios !== undefined)
            ? browserOptions.ios
            : this.defaultIosBrowserOptions;
      } else {
        iabOptions =
          browserOptions &&
          (browserOptions.android !== null ||
            browserOptions.android !== undefined)
            ? browserOptions.android
            : this.defaultAndroidBrowserOptions;
      }
      this.iabObject = this.iab.create(normalizedUrl, '_blank', iabOptions);
      if (this.platform.is('cordova')) {
        this.iabObject.on('loadstart').subscribe(event => {
          this.setResultOnLoadStart(event);
        });
        this.iabObject.on('exit').subscribe(_ => {
          this.checkResultOnExit();
        });
      } else {
        this.viewClosed();
        this.resetData();
      }
    });
  }

  close() {
    if (this.iabObject) {
      this.iabObject.close();
    }
  }

  private setResultOnLoadStart = (loadStartEvent: any) => {
    const handler = this.getUrlHandler(loadStartEvent.url);
    if (handler) {
      this.resultDetails = getQueryParamsObject(loadStartEvent.url);
      this.resultHandler = handler;
      this.iabObject.close();
    }
  };

  private getUrlHandler(url: string) {
    if (!url) {
      return null;
    }
    return this.handlers.find(handler => {
      return (
        handler &&
        handler.url &&
        url.toLowerCase().startsWith(handler.url.toLowerCase())
      );
    });
  }

  private checkResultOnExit = () => {
    if (this.resultHandler != null) {
      const { handlerFunction } = this.resultHandler;
      handlerFunction(this.resultDetails, this.payloadData);
    } else {
      this.viewClosed();
    }
    this.resetData();
  };

  private viewClosed() {
    if (this.onViewClosedHandler != null) {
      this.onViewClosedHandler(null, this.payloadData);
    }
  }

  private resetData() {
    this.resultHandler = null;
    this.payloadData = null;
    this.resultDetails = null;
    this.onViewClosedHandler = null;
    this.handlers = [];
  }
}
